import React, { useEffect, useState } from 'react';
import { ChevronDown, CheckCircle2, PlayCircle, Lock, Circle } from 'lucide-react';
import { useLang, pickLocalized } from '../../i18n/LanguageContext';
import { fmtClock, lessonSecs } from '../../lib/curriculum';

const THEME = {
  light: {
    row: 'hover:bg-slate-50', rowActive: 'bg-blue-50', title: 'text-slate-700 font-medium', titleActive: 'text-blue-700 font-extrabold',
    meta: 'text-slate-400', play: 'text-slate-300', playActive: 'text-blue-600', dot: 'text-slate-300 fill-slate-300',
    group: '', groupActive: 'bg-blue-50/30', head: 'text-slate-900', headActive: 'text-blue-700', counter: 'text-slate-400', wrap: 'divide-y divide-slate-100', chevron: 'text-slate-600',
  },
  dark: {
    row: 'hover:bg-white/5', rowActive: 'bg-blue-600 shadow-lg shadow-blue-600/30', title: 'text-slate-200 font-medium', titleActive: 'text-white font-extrabold',
    meta: 'text-slate-400', play: 'text-slate-400', playActive: 'text-white', dot: 'text-blue-500 fill-blue-500',
    group: 'rounded-2xl bg-white/[0.04] border border-white/5', groupActive: 'rounded-2xl bg-white/[0.06] border border-blue-500/40', head: 'text-white', headActive: 'text-white', counter: 'text-slate-300 bg-white/10 rounded-lg px-2 py-0.5', wrap: 'space-y-3 p-3', chevron: 'text-slate-400',
  },
  compact: {
    row: 'hover:bg-slate-50', rowActive: 'bg-blue-50 text-blue-700', title: 'text-slate-600', titleActive: 'text-blue-700 font-semibold',
    meta: 'text-slate-400', play: 'text-slate-300', playActive: 'text-blue-600', dot: 'text-slate-300 fill-slate-300',
    group: '', groupActive: '', head: 'text-slate-800', headActive: 'text-blue-700', counter: 'text-slate-400', wrap: 'divide-y divide-slate-100', chevron: 'text-slate-400',
  },
};

const LessonItem = ({ lesson, active, done, locked, onClick, th, dark, compact }) => {
  const { lang } = useLang();
  const title = pickLocalized(lesson.title, lang);
  return (
    <button type="button" onClick={onClick} data-testid={`curriculum-lesson-${lesson.id}`} title={compact ? title : undefined} aria-current={active ? 'true' : undefined}
      className={`relative w-full flex items-center text-start transition-colors ${compact ? 'gap-2.5 ps-3 pe-2.5 py-2 rounded-lg' : 'gap-3 px-4 py-3 rounded-xl'} ${active ? th.rowActive : th.row}`}>
      {compact && active && <span className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-blue-600" />}
      <span className={`shrink-0 flex justify-center ${compact ? 'w-4' : 'w-6'}`}>
        {done ? <CheckCircle2 data-testid={`lesson-done-${lesson.id}`} className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${dark ? 'text-white fill-emerald-500' : 'text-emerald-500'}`} />
          : locked ? <Lock className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-amber-400`} />
          : active ? (compact ? <PlayCircle className="w-4 h-4 text-blue-600" /> : <span className={`w-3 h-3 rounded-full ${dark ? 'bg-white/80' : 'bg-blue-600 ring-4 ring-blue-100'}`} />)
          : <Circle className={`w-2 h-2 ${th.dot}`} />}
      </span>
      <span className={`flex-1 min-w-0 leading-snug ${compact ? 'text-[13px] line-clamp-2 break-words' : 'text-sm'} ${active ? th.titleActive : th.title}`}>
        {!dark && !compact && <span className="text-slate-400 font-normal me-1.5" dir="ltr">{lesson.num}</span>}{title}
      </span>
      {lessonSecs(lesson) ? <span className={`shrink-0 ${compact ? 'text-[11px]' : 'text-xs'} ${active && dark ? 'text-blue-100' : th.meta}`} dir="ltr">{fmtClock(lessonSecs(lesson))}</span> : null}
      {!compact && <PlayCircle className={`shrink-0 w-5 h-5 ${active ? th.playActive : th.play}`} />}
    </button>
  );
};

export const CurriculumList = ({ groups, activeId, completed, isLocked, onSelect, dark = false, compact = false }) => {
  const { lang } = useLang();
  const th = THEME[compact ? 'compact' : dark ? 'dark' : 'light'];
  const activeGroup = groups.find(g => g.lessons.some(l => l.id === activeId))?.id;
  const [open, setOpen] = useState(() => new Set([activeGroup || groups[0]?.id]));
  useEffect(() => { if (activeGroup) setOpen(s => new Set(s).add(activeGroup)); }, [activeGroup]);
  const toggle = (id) => setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div data-testid="curriculum-list" className={th.wrap}>
      {groups.map((g, gi) => {
        const isOpen = open.has(g.id);
        const done = g.lessons.filter(l => completed.has(l.id)).length;
        const hasActive = g.id === activeGroup;
        return (
          <div key={g.id} className={`relative ${hasActive ? th.groupActive : th.group}`}>
            {hasActive && !dark && !compact && <span className="absolute inset-y-0 start-0 w-1 bg-blue-600 rounded-full" />}
            <button type="button" onClick={() => toggle(g.id)} data-testid={`curriculum-section-${g.id}`} aria-expanded={isOpen}
              className={`w-full flex items-center text-start ${compact ? 'gap-2 px-3 py-2.5 hover:bg-slate-50 transition-colors' : 'gap-3 px-4 py-4'}`}>
              {dark ? <span className="text-slate-400 text-sm font-bold" dir="ltr">{gi + 1}.</span> : <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${th.chevron} ${isOpen ? 'rotate-180' : ''}`} />}
              <span className={`flex-1 min-w-0 ${compact ? 'font-bold text-[13px] leading-snug line-clamp-2' : 'font-extrabold text-sm sm:text-base'} ${hasActive ? th.headActive : th.head}`}>{pickLocalized(g.title, lang)}</span>
              <span className={`text-xs shrink-0 ${th.counter}`} dir="ltr">{done} / {g.lessons.length}</span>
            </button>
            {isOpen && (
              <div className={`${compact ? 'px-1.5 pb-2' : 'px-2 pb-3'} space-y-0.5 ${dark ? 'border-t border-white/5 pt-2' : ''}`}>
                {g.lessons.map(l => (
                  <LessonItem key={l.id} lesson={l} th={th} dark={dark} compact={compact} active={l.id === activeId} done={completed.has(l.id)} locked={isLocked(l)} onClick={() => onSelect(l)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
